import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useSearchContext } from "../context/SearchContext";
import "../styles/Search.css";
import { countries, states, districts } from "../data/locationData";
import { languages } from "../components/LanguageSelector";
// Removed: import Navbar from "../components/Navbar";
import categories from "../data/categories";
import bookGif from "../images/book.gif";
import Transliterate from "../components/Transliterate";
import { LanguageContext } from "../context/LanguageContext";

const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition;
const recognition = SpeechRecognition ? new SpeechRecognition() : null;

const Search = () => {
  const { searchData, setSearchData } = useSearchContext();
  const { query, results, aiQuery, noResults } = searchData;
  const { selectedLanguage } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("commonman");
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [popupVisible, setPopupVisible] = useState(false);
  const [isListening, setIsListening] = useState(false);
  // Inline validation (replaces native alert popups)
  const [validationError, setValidationError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ country: false, state: false, query: false });
  const validationRef = useRef(null);

  // ...existing code...
  const suggestionsRef = useRef(null);
  const situationInputRef = useRef(null);
  const location = useLocation();

  // Bring the validation message into view so it is never off-screen.
  useEffect(() => {
    if (validationError && validationRef.current) {
      validationRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [validationError]);

  // Hide suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        situationInputRef.current &&
        !situationInputRef.current.contains(event.target)
      ) {
        setFilteredCategories([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const storedCountry = localStorage.getItem("selectedCountry");
    const storedState = localStorage.getItem("selectedState");
    const storedDistrict = localStorage.getItem("selectedDistrict");
    const storedLevel = localStorage.getItem("selectedLevel");

    if (storedCountry) setSelectedCountry(storedCountry);
    if (storedState) setSelectedState(storedState);
    if (storedDistrict) setSelectedDistrict(storedDistrict);
    if (storedLevel) setSelectedLevel(storedLevel);
  }, []);

  useEffect(() => {
    if (location.state?.openDetailedSearch) {
      setPopupVisible(true);
      if (location.state.aiQuery) {
        setSearchData((prevState) => ({
          ...prevState,
          aiQuery: location.state.aiQuery,
        }));
      }
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, setSearchData, navigate, location.pathname]);

  useEffect(() => {
    if (!supportedLanguages.includes(selectedLanguage)) return;

    const loadTransliteration = () => {
      const control = new window.google.elements.transliteration.TransliterationControl({
        sourceLanguage: "en",
        destinationLanguage: [selectedLanguage],
        transliterationEnabled: true,
      });

      if (situationInputRef.current) {
        control.makeTransliteratable([situationInputRef.current]);
      }
    };
          
    const interval = setInterval(() => {
      if (window.google?.elements?.transliteration) {
        loadTransliteration();
        clearInterval(interval);
      }
    }, 300);

    return () => clearInterval(interval);
  }, [selectedLanguage]);

  const handleCountryChange = (country) => {
    setValidationError("");
    setFieldErrors((prev) => ({ ...prev, country: false, state: false }));
    setSelectedCountry(country);
    localStorage.setItem("selectedCountry", country);
    setSelectedState("");
    setSelectedDistrict("");
    localStorage.removeItem("selectedState");
    localStorage.removeItem("selectedDistrict");
  };

  const handleStateChange = (state) => {
    setValidationError("");
    setFieldErrors((prev) => ({ ...prev, state: false }));
    setSelectedState(state);
    localStorage.setItem("selectedState", state);
    setSelectedDistrict("");
    localStorage.removeItem("selectedDistrict");
  };

  const handleDistrictChange = (district) => {
    setSelectedDistrict(district);
    localStorage.setItem("selectedDistrict", district);
  };

  const handleLevelChange = (level) => {
    setSelectedLevel(level);
    localStorage.setItem("selectedLevel", level);
  };

  const extractLawCategory = (aiResponse) => {
    if (!aiResponse) return "Not Categorized";
    const categoryRegex = /Law Category:\s*(.*?)(?:\n|$)/i;
    const match = aiResponse.match(categoryRegex);
    return match && match[1] ? match[1].trim() : "Not Categorized";
  };

  const handleAiSearchOpen = () => setPopupVisible(true);
  const handleAiSearchClose = () => {
    setPopupVisible(false);
    stopListening();
  };

  const startListening = () => {
    if (!recognition || isListening) return;

    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;

    recognition.start();
    setIsListening(true);

    recognition.onresult = (event) => {
      let finalTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        finalTranscript += event.results[i][0].transcript;
      }
      setSearchData((prev) => ({ ...prev, aiQuery: finalTranscript }));
    };

    recognition.onerror = (event) => {
      setValidationError("Speech recognition error: " + event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };
  };

  const stopListening = () => {
    if (recognition && isListening) {
      recognition.stop();
      setIsListening(false);
    }
  };

  const handleMicClick = () => {
    setPopupVisible(true);
    startListening();
  };

  const handleAiSearch = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    if (!aiQuery.trim()) return;

    setAiLoading(true);
    setSearchData({ ...searchData, aiResults: [], noResults: false });

    try {
      const aiEndpoint = selectedLanguage && selectedLanguage !== "en" ? "/api/deepseek-search" : "/api/ai-search";
      const response = await axios.post(aiEndpoint, {
        userInput: aiQuery,
        country: selectedCountry,
        state: selectedState,
        level: selectedLevel,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await axios.post("/api/contribution/save-contribution", {
        columnOne: aiQuery,
        columnTwo: response.data.aiResponse,
        selectedCountry,
        selectedState,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const extractedCategory = extractLawCategory(response.data.aiResponse);

      navigate("/full-content", {
        state: {
          content: response.data.aiResponse,
          summary: response.data.aiResponse,
          query: aiQuery,
          lawCategory: extractedCategory,
          country: selectedCountry,
          state: selectedState,
          district: selectedDistrict,
          level: selectedLevel,
        },
      });
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
        return;
      }
      setSearchData({
        ...searchData,
        results: [
          ...results,
          {
            content: "Please fill out all the necessary fields.",
            isAI: true,
          },
        ],
      });
    } finally {
      setAiLoading(false);
      handleAiSearchClose();
    }
  };

  const handleSearch = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const errors = {
      country: !selectedCountry,
      state: !selectedState,
      query: !query.trim(),
    };

    if (errors.country || errors.state || errors.query) {
      setFieldErrors(errors);
      const missing = [];
      if (errors.query) missing.push("enter your legal query");
      if (errors.country) missing.push("select a country");
      if (errors.state) missing.push("select a state");
      const message =
        missing.length === 1
          ? `Please ${missing[0]}.`
          : `Please ${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}.`;
      setValidationError(message);
      return;
    }

    setFieldErrors({ country: false, state: false, query: false });
    setValidationError("");

    setLoading(true);
    setAiLoading(true);
    setSearchData({ ...searchData, results: [], noResults: false });

    let aiResult = null;
    let dbResults = [];
    let aiError = false;
    let dbError = false;

    await Promise.all([
      (async () => {
        try {
          const aiEndpoint = selectedLanguage && selectedLanguage !== "en" ? "/api/deepseek-search" : "/api/ai-search";
          const response = await axios.post(aiEndpoint, {
            userInput: query,
            country: selectedCountry,
            state: selectedState,
            level: selectedLevel,
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          aiResult = {
            query,
            content: response.data.aiResponse,
            country: selectedCountry,
            state: selectedState,
            isAI: true,
          };
          await axios.post("/api/contribution/save-contribution", {
            columnOne: query,
            columnTwo: response.data.aiResponse,
            selectedCountry,
            selectedState,
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          aiError = true;
        }
      })(),
      (async () => {
        try {
          const response = await axios.get("/api/lawSearchRoute/search", {
            params: { query, selectedCountry, selectedState },
            headers: { Authorization: `Bearer ${token}` }
          });
          dbResults = response.data.results.map((item) => ({
            query: item.query,
            content: item.content,
            country: item.country,
            state: item.state,
            isAI: false,
          }));
        } catch (error) {
          dbError = true;
        }
      })()
    ]);

    // Instead of setting results, navigate to FullContent with both answers
    if (aiResult) {
      const extractedCategory = extractLawCategory(aiResult.content);
      
      navigate("/full-content", {
        state: {
          content: aiResult.content,
          summary: aiResult.content,
          query,
          lawCategory: extractedCategory,
          country: selectedCountry,
          state: selectedState,
          district: selectedDistrict,
          level: selectedLevel,
          dbResults, // pass db answers
        },
      });
    } else {
      setSearchData({
        ...searchData,
        results: dbResults,
        noResults: dbResults.length === 0,
      });
    }
    setLoading(false);
    setAiLoading(false);
  };

  const handleCategoryFilter = (input) => {
    const filtered = categories.filter((category) =>
      category.toLowerCase().startsWith(input.toLowerCase())
    );
    setFilteredCategories(filtered);
  };

  const handleSuggestionSelect = (suggestion) => {
    setSearchData((prevState) => ({ ...prevState, query: suggestion }));
    setFilteredCategories([]);
  };

  const handleViewContent = (result) => {
    navigate("/full-content", {
      state: {
        content: result.content || "",
        query: result.query || "No query available",
        summary: result.summary || "",
        lawCategory: result.lawCategory || "Not Categorized",
        country: result.country || "Unknown country",
        state: result.state || "Unknown state",
        district: selectedDistrict || "Unknown district",
        level: selectedLevel,
      },
    });
  };

  const rtlLanguages = ["ar", "ur", "fa", "he"];
  const isRTL = rtlLanguages.includes(selectedLanguage);
  const supportedLanguages = ["hi", "ta", "te", "gu", "kn", "ml", "bn", "pa", "ne", "mr", "ur","ar","ru","sa","si","sr","el","zh","mr","or",];

  return (
    <div>
      {/* Removed page-level <Navbar /> to avoid duplication; App.js already provides it */}
      <div className="search-container">
        <h2 className="h">Find Your Legal Solutions</h2>

        {validationError && (
          <div
            className="search-validation-msg"
            ref={validationRef}
            role="alert"
            aria-live="assertive"
          >
            <span className="search-validation-icon" aria-hidden="true">⚠️</span>
            {validationError}
          </div>
        )}

        <div className="dropdown-container">
          <div className="dropdown-step">
            <label>Country:</label>
            <select
              value={selectedCountry}
              onChange={(e) => handleCountryChange(e.target.value)}
              className={fieldErrors.country ? "input-error" : ""}
            >
              <option value="">-- Select Country --</option>
              {countries.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>
       
          {selectedCountry && (
            <div className="dropdown-step">
              <label>State:</label>
              <select
                value={selectedState}
                onChange={(e) => handleStateChange(e.target.value)}
                className={fieldErrors.state ? "input-error" : ""}
              >
                <option value="">-- Select State --</option>
                {states[selectedCountry]?.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
          )}

          {selectedState && (
            <div className="dropdown-step">
              <label>District:</label>
              <select
                value={selectedDistrict}
                onChange={(e) => handleDistrictChange(e.target.value)}
              >
                <option value="">-- Select District --</option>
                {districts[selectedState]?.map((district) => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="dropdown-container level-dropdown-container">
          <div className="dropdown-step level-dropdown-step">
            <label>Search Level:</label>
            <select
              value={selectedLevel}
              onChange={(e) => handleLevelChange(e.target.value)}
              className="level-selector"
            >
              <option value="commonman">👨🏻‍💼 Public Level (Simple & Easy)</option>
              <option value="professional">🧑‍💼 Professional Level (Advocate & Advisor)</option>
              <option value="academic">👨🏻‍🎓 Academic Level (Scholarly & Detailed)</option>
            </select>
          </div>
        </div>

        <div className="search-bar large-search-bar">
          <textarea
            ref={situationInputRef}
            placeholder={(() => {
              const langObj = languages.find(l => l.code === selectedLanguage);
              const langDisplay = langObj ? langObj.native : selectedLanguage;
              return `Enter your problem in detail or Select a topic from suggestions. Type only in ${langDisplay}.`;
            })()}
            value={searchData.query}
            onChange={(e) => {
              const input = e.target.value;
              setSearchData({ ...searchData, query: input, aiQuery: input });
              handleCategoryFilter(input);
              if (input.trim()) {
                setFieldErrors((prev) => ({ ...prev, query: false }));
                setValidationError("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSearch();
              }
            }}
            dir={isRTL ? "rtl" : "ltr"}
            className={`large-search-textarea ${fieldErrors.query ? "input-error" : ""}`}
            rows={6}
          />
          <div className="search-bar-actions">
            <button onClick={handleMicClick} title="Voice Search" className="mic-btn notranslate" translate="no">
              <span className="notranslate" translate="no" aria-hidden="true">🎙️</span>
            </button>
            <button onClick={handleSearch} disabled={loading || aiLoading} className="notranslate" translate="no">
              {(loading || aiLoading)
                ? <img src={bookGif} alt="Loading..." className="book-loader notranslate" translate="no" />
                : <span className="notranslate" translate="no">Search</span>}
            </button>
          </div>
          {aiLoading && selectedLanguage && selectedLanguage !== "en" && (
            <div className="deep-analysis-message">
              We're performing a deeper analysis — this may take a little longer than usual.
            </div>
          )}
        </div>

        {filteredCategories.length > 0 && (
          <div className="suggestions" ref={suggestionsRef}>
            {filteredCategories.map((category) => (
              <div
                key={category}
                className="suggestion-item"
                onClick={() => handleSuggestionSelect(category)}
              >
                {category}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;